import type { EmployeeListItem } from '@/api/employees.api';
import type { SeatView } from '@/api/seats.api';

/**
 * Build a hierarchical **seat** tree from a flat list of seats — the
 * seat-driven successor to `buildOrgTree`. The accountability chart now
 * renders SEATS (roles), not the employee-manager tree; reporting lines
 * are derived from seat ancestry server-side.
 *
 * Same defensive properties as `buildOrgTree`, adapted to `parentSeatId`:
 *
 *   1. **Multiple roots** — any seat with no `parentSeatId` is a root.
 *
 *   2. **Never hide a seat** — a seat whose `parentSeatId` points at a
 *      seat NOT in the input (filtered out, archived, cross-scenario)
 *      is surfaced as a root rather than silently dropped. Broken by a
 *      cycle → same treatment. The chart must never lose a seat.
 *
 *   3. **Cycle detection** — client-side cycle-break (the schema allows
 *      cycles via independent writes; the server guards, but a stale
 *      client read could still contain one). The offending edge is
 *      recorded in `brokenCycleEdges` and the seat becomes a root.
 *
 *   4. **Stable sort** — siblings by `order` ASC, then `name`, then `id`.
 *
 * Layout math (d3-hierarchy) runs on top of this pure structure.
 */

export interface SeatTreeNode {
  seat: SeatView;
  /** Child seats, sorted. */
  children: SeatTreeNode[];
  /** Depth from root (0 = root). */
  depth: number;
}

export interface SeatTreeResult {
  /** Top-level seats (no parent) PLUS surfaced orphans/cycle-broken seats. */
  roots: SeatTreeNode[];
  /** Total seats placed — should equal the input length (nothing dropped). */
  totalNodes: number;
  /** Parent edges removed to keep the structure a tree; surfaced for debug. */
  brokenCycleEdges: { fromId: string; toId: string }[];
}

interface InternalNode {
  seat: SeatView;
  childIds: string[];
}

export function buildSeatTree(seats: SeatView[]): SeatTreeResult {
  if (seats.length === 0) {
    return { roots: [], totalNodes: 0, brokenCycleEdges: [] };
  }

  const byId = new Map<string, InternalNode>();
  for (const s of seats) {
    byId.set(s.id, { seat: s, childIds: [] });
  }

  // Seats that get promoted to the top level because their declared parent
  // can't hold them (missing parent, or the edge would close a cycle).
  const promotedToRoot = new Set<string>();
  const brokenCycleEdges: { fromId: string; toId: string }[] = [];

  for (const s of seats) {
    if (!s.parentSeatId) continue;
    const parent = byId.get(s.parentSeatId);
    if (!parent) {
      // Parent not in the input — surface as a root, never drop.
      promotedToRoot.add(s.id);
      continue;
    }
    if (createsCycle(byId, s.id, s.parentSeatId)) {
      brokenCycleEdges.push({ fromId: s.id, toId: s.parentSeatId });
      promotedToRoot.add(s.id);
      continue;
    }
    parent.childIds.push(s.id);
  }

  const rootIds: string[] = [];
  for (const s of seats) {
    if (!s.parentSeatId || promotedToRoot.has(s.id)) {
      rootIds.push(s.id);
    }
  }

  const roots = rootIds.map((id) => materialize(byId, id, 0, new Set<string>())).filter(isPresent);
  sortTreeSiblings(roots);

  return {
    roots,
    totalNodes: countNodes(roots),
    brokenCycleEdges,
  };
}

/**
 * Employees who hold no (non-archived) seat in the given set — the chart's
 * "bench". Keeps the never-hide-anyone property: a person without a seat
 * still appears on the page. Preserves input order; the page decides which
 * employees (active/all) to pass in and which scenario's seats to compare
 * against.
 */
export function employeesWithoutSeat(employees: EmployeeListItem[], seats: SeatView[]): EmployeeListItem[] {
  const seated = new Set<string>();
  for (const s of seats) {
    if (s.archivedAt) continue;
    if (s.holderEmployeeId) seated.add(s.holderEmployeeId);
  }
  return employees.filter((e) => !seated.has(e.id));
}

function createsCycle(byId: Map<string, InternalNode>, attachId: string, parentId: string): boolean {
  let cursor: string | undefined = parentId;
  const visited = new Set<string>();
  while (cursor) {
    if (cursor === attachId) return true;
    if (visited.has(cursor)) return true; // pre-existing cycle upstream
    visited.add(cursor);
    const node = byId.get(cursor);
    cursor = node?.seat.parentSeatId ?? undefined;
  }
  return false;
}

function materialize(
  byId: Map<string, InternalNode>,
  id: string,
  depth: number,
  pathSeen: Set<string>,
): SeatTreeNode | null {
  if (pathSeen.has(id)) return null; // belt-and-suspenders cycle guard
  const node = byId.get(id);
  if (!node) return null;
  pathSeen.add(id);
  const children = node.childIds
    .map((cid) => materialize(byId, cid, depth + 1, new Set(pathSeen)))
    .filter(isPresent);
  return { seat: node.seat, children, depth };
}

function sortTreeSiblings(nodes: SeatTreeNode[]): void {
  nodes.sort(compareSeats);
  for (const n of nodes) sortTreeSiblings(n.children);
}

function compareSeats(a: SeatTreeNode, b: SeatTreeNode): number {
  if (a.seat.order !== b.seat.order) return a.seat.order - b.seat.order;
  const byName = a.seat.name.localeCompare(b.seat.name);
  if (byName !== 0) return byName;
  return a.seat.id.localeCompare(b.seat.id);
}

function countNodes(nodes: SeatTreeNode[]): number {
  let total = 0;
  for (const n of nodes) total += 1 + countNodes(n.children);
  return total;
}

function isPresent<T>(v: T | null | undefined): v is T {
  return v !== null && v !== undefined;
}
